import { useEffect, useMemo, useRef, useState } from "react";
import { CARD, BD, TX, TX2, GR, BL, YL, RD, PU } from "../../utils/constants";
import { SECTORS } from "../../utils/constants";
import { scoreColor, biasColor, isNum, fmtPrice, fmtPct } from "../../utils/formatUtils";

/**
 * Row heights, measured from the rendered table rather than estimated.
 *
 * Rows come in exactly two variants: a plain row, and one carrying an earnings
 * or insider badge, which adds a second line inside the name cell. Because the
 * two are deterministic, the exact document offset of every row can be computed
 * without measuring the DOM — so windowing stays pixel-accurate and the
 * scrollbar does not drift or jump, which is the usual failure of virtualised
 * lists built on an estimated average height.
 */
const ROW_H = 29;
const ROW_H_BADGE = 33;

/**
 * Below this many rows the whole list is rendered as before.
 *
 * Windowing costs a scroll listener and some arithmetic; for a short filtered
 * list that is pure overhead, and rendering everything keeps behaviour
 * identical to what shipped previously.
 */
const VIRTUALIZE_ABOVE = 120;

/** Rows drawn beyond the viewport, so a fast flick doesn't reveal blank space. */
const OVERSCAN = 10;

/** First-paint window, before the scroll listener has measured anything. */
const INITIAL_ROWS = 60;

const hasBadge = (s) => !!(s.earningsFlag || s.insiderSignal);

/**
 * Cumulative pixel offset of every row, plus the total height.
 *
 * offsets[i] is where row i starts; offsets[length] is the full height.
 * Exported so the windowing arithmetic can be tested directly — an off-by-one
 * here shows up as rows that flicker or a scrollbar that drifts, which is
 * tedious to catch by eye and trivial to catch with a test.
 */
export function computeOffsets(rows) {
  const offsets = new Array(rows.length + 1);
  let acc = 0;
  for (let i = 0; i < rows.length; i++) {
    offsets[i] = acc;
    acc += hasBadge(rows[i]) ? ROW_H_BADGE : ROW_H;
  }
  offsets[rows.length] = acc;
  return { offsets, totalHeight: acc };
}

/**
 * The slice of rows intersecting the viewport, padded by OVERSCAN either side.
 *
 * viewTop/viewBottom are in the row list's own coordinate space — the distance
 * scrolled past the top of the table, not the page.
 */
export function visibleRange(offsets, count, viewTop, viewBottom, overscan = OVERSCAN) {
  if (count === 0) return { start: 0, end: 0 };
  let lo = 0, hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid + 1] <= viewTop) lo = mid + 1; else hi = mid;
  }
  const start = Math.max(0, lo - overscan);
  let end = start;
  while (end < count && offsets[end] < viewBottom) end++;
  return { start, end: Math.min(count, end + overscan) };
}

export default function StocksTable({ filtered, sector, setSector, search, setSearch, watchlist, onToggleWL, onStockClick }) {
  const rowsRef = useRef(null);

  // Recomputed only when the list changes; scrolling is then a binary search.
  const { offsets, totalHeight } = useMemo(() => computeOffsets(filtered), [filtered]);

  /** Membership test per row was O(watchlist); a Set keeps the render linear. */
  const watchSet = useMemo(() => new Set(watchlist || []), [watchlist]);

  const virtualize = filtered.length > VIRTUALIZE_ABOVE;

  /**
   * The first window is sized before any effect runs.
   *
   * Effects fire after the first paint, so initialising to the whole list
   * would render all 932 rows once and only then trim — paying the entire cost
   * this exists to avoid. INITIAL_ROWS covers a tall viewport with overscan.
   */
  const [range, setRange] = useState(() => ({
    start: 0,
    end: filtered.length > VIRTUALIZE_ABOVE ? Math.min(filtered.length, INITIAL_ROWS) : filtered.length,
  }));

  useEffect(() => {
    if (!virtualize) { setRange({ start: 0, end: filtered.length }); return; }

    /**
     * The page itself scrolls — the table is not its own scroll box — so the
     * visible slice is derived from the window scroll position and the table's
     * offset within the document. Keeping the native page scroll preserves
     * touch behaviour, momentum and the browser's own scrollbar.
     */
    const compute = () => {
      const el = rowsRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const viewTop = window.scrollY - docTop;
      const viewBottom = viewTop + window.innerHeight;

      const { start, end } = visibleRange(offsets, filtered.length, viewTop, viewBottom);
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [virtualize, filtered.length, offsets]);

  // A filter or sort change can leave a stale range pointing past the new list.
  const start = virtualize ? Math.min(range.start, Math.max(0, filtered.length - 1)) : 0;
  const end = virtualize ? Math.min(Math.max(range.end, start), filtered.length) : filtered.length;
  const visible = filtered.slice(start, end);
  const padTop = virtualize ? offsets[start] : 0;
  const padBottom = virtualize ? Math.max(0, totalHeight - offsets[end]) : 0;

  const GRID = "22px 56px 1fr 58px 60px 36px 36px 36px 56px 72px 68px 18px";

  return (
    <div>
      <div style={{ display: "flex", gap: 3, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap", flex: 1 }}>
          {SECTORS.map((s) => (
            <button key={s} className="btn" onClick={() => setSector(s)} style={{ background: sector === s ? GR + "18" : CARD, border: `1px solid ${sector === s ? GR + "50" : BD}`, borderRadius: 5, padding: "4px 10px", color: sector === s ? GR : TX2, fontSize: 8, letterSpacing: 1 }}>{s.toUpperCase()}</button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 6, padding: "5px 10px", color: TX, fontSize: 9, width: 120 }} />
      </div>

      <div style={{ overflowX: "auto" }}>
      <div className="ts-panel" style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, overflow: "hidden", marginBottom: 8, minWidth: 680 }}>
        <div style={{ display: "grid", gridTemplateColumns: GRID, padding: "6px 12px", borderBottom: `1px solid ${BD}`, fontSize: 7, color: TX2, letterSpacing: 2 }}>
          <div>#</div><div>SYM</div><div>NAME</div><div>PRICE</div><div>TODAY</div><div>SCR</div><div>MOM</div><div>TRD</div><div>5D%</div><div>BIAS</div><div>LINKS</div><div>★</div>
        </div>
        <div ref={rowsRef}>
          {/* Spacers stand in for the rows above and below the window, so the
              page keeps its true height and the scrollbar behaves normally. */}
          {padTop > 0 && <div style={{ height: padTop }} aria-hidden="true" />}
          {visible.map((s, i) => {
            const rank = start + i + 1;
            return (
          <div key={s.symbol} className="hov-row" onClick={() => onStockClick(s)} style={{ display: "grid", gridTemplateColumns: GRID, padding: "6px 12px", borderBottom: `1px solid ${BD}`, borderLeft: `2px solid ${s.earningsFlag === "upcoming" ? "#ffd740" : scoreColor(s.score)}`, alignItems: "center" }}>
            <div style={{ fontSize: 8, color: TX2 }}>{rank}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <span style={{ fontWeight: 700, color: TX, fontSize: 10, fontFamily: "'Space Grotesk',sans-serif" }}>{s.symbol}</span>
              {s.isFMP     && <span style={{ fontSize: 6, color: BL  }}>◆</span>}
              {s.isDynamic && <span style={{ fontSize: 6, color: YL }}>●</span>}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: 8, color: TX2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
              {(s.earningsFlag || s.insiderSignal) && (
                <div style={{ display: "flex", gap: 2, marginTop: 1 }}>
                  {s.earningsFlag === "upcoming"    && <span style={{ fontSize: 6, background: "#ffd74018", color: "#ffd740", border: "1px solid #ffd74040", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>EARN</span>}
                  {s.earningsFlag === "recent_up"   && <span style={{ fontSize: 6, background: "#00e67618", color: "#00e676", border: "1px solid #00e67640", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>EARN↑</span>}
                  {s.earningsFlag === "recent_down" && <span style={{ fontSize: 6, background: "#ef535018", color: "#ef5350", border: "1px solid #ef535040", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>EARN↓</span>}
                  {s.insiderSignal === "buy"         && <span style={{ fontSize: 6, background: "#00e67618", color: "#00e676", border: "1px solid #00e67640", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>INS↑</span>}
                  {s.insiderSignal === "sell"        && <span style={{ fontSize: 6, background: "#ef535018", color: "#ef5350", border: "1px solid #ef535040", borderRadius: 3, padding: "0 3px", whiteSpace: "nowrap" }}>INS↓</span>}
                </div>
              )}
            </div>
            <div style={{ fontSize: 9, color: isNum(s.price) ? TX : TX2 }}>{fmtPrice(s.price)}</div>
            <div style={{ fontSize: 9, color: !isNum(s.change) ? TX2 : s.change >= 0 ? GR : RD, fontWeight: 600 }}>{fmtPct(s.change)}</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: scoreColor(s.score), fontFamily: "'Space Grotesk',sans-serif" }}>{s.score}</div>
            <div style={{ fontSize: 9, color: PU }}>{s.momentum}</div>
            <div style={{ fontSize: 9, color: GR }}>{s.trend}</div>
            <div style={{ fontSize: 9, color: s.fiveDayReturn == null ? TX2 : s.fiveDayReturn >= 0 ? GR : RD }}>{s.fiveDayReturn != null ? (s.fiveDayReturn >= 0 ? "+" : "") + s.fiveDayReturn.toFixed(1) + "%" : "—"}</div>
            <div style={{ fontSize: 8, fontWeight: 600, color: biasColor(s.tomorrowBias) }}>{s.technicalBias ?? s.tomorrowBias}</div>
            <div style={{ display: "flex", gap: 3 }}>
              <a href={`https://finance.yahoo.com/quote/${s.symbol}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 7, color: BL, textDecoration: "none", border: `1px solid ${BD}`, borderRadius: 3, padding: "1px 4px" }}>Y</a>
              <a href={`https://www.tradingview.com/chart/?symbol=${s.symbol}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 7, color: GR, textDecoration: "none", border: `1px solid ${BD}`, borderRadius: 3, padding: "1px 4px" }}>TV</a>
              <a href={`https://stockanalysis.com/stocks/${s.symbol.toLowerCase()}/`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 7, color: YL, textDecoration: "none", border: `1px solid ${BD}`, borderRadius: 3, padding: "1px 4px" }}>SA</a>
            </div>
            <div className="star" onClick={(e) => { e.stopPropagation(); onToggleWL(s.symbol); }} style={{ fontSize: 10, color: watchSet.has(s.symbol) ? YL : TX2 }}>{watchSet.has(s.symbol) ? "★" : "☆"}</div>
          </div>
            );
          })}
          {padBottom > 0 && <div style={{ height: padBottom }} aria-hidden="true" />}
        </div>
      </div>
      </div>
      <div style={{ fontSize: 8, color: TX2 }}>
        Educational tool only. Not financial advice. <span style={{ color: YL }}>●</span> = dynamic mover  <span style={{ color: BL }}>◆</span> = FMP live screener pick
        {virtualize && <span> · showing {visible.length} of {filtered.length} rows as you scroll</span>}
      </div>
    </div>
  );
}
